import React, { useState, useEffect } from 'react';
import { Header, Loader, Icon } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import ReactGA from 'react-ga4';
import toast from 'react-hot-toast';

import { DealStates } from '~/constants';
import { indexRfisByProjectId } from '~/api/requestForInformation';
import * as projectApi from '~/api/projects';
import * as portfolioApi from '~/api/portfolios';
import * as dealSpreadsheetApi from '~/api/dealSpreadsheet';
import { logUserActivity, UserActivityTypes } from '~/api/user';
import { useProjectContext, usePortfolioContext } from '~/hooks';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { useDataroomQuery } from '~/requests/dataroom/useDataroomQuery';
import { getDataroomCounts } from '~/utils/calculators/dataroomCounts';
import { downloadPortfolioExcel, downloadProjectExcel, generateDealSpreadsheet, generateProjectSpreadsheet } from '~/utils';

const ProjectChecklist: React.FC = () => {
    const { project, formattedProject, projectCosts, projectDisplayValues } = useProjectContext();
    const { portfolio, displayBlobs, portfolioTotals, locations } = usePortfolioContext();
    const { permissions } = usePermissions();
    const bidDeadline = project?.portfolioId
        ? project?.portfolio?.bidDeadline || null
        : project?.bidDeadline || null;
    const [rfis, setRfis] = useState<any>();
    const [showChecklist, setShowChecklist] = useState<boolean>(true);
    const [hasSummaryDoc, setHasSummaryDoc] = useState<boolean | undefined>(undefined);
    const activity = project?.currentUserActivity;
    // Mirror the dataroom tab visibility (see ProjectPage): hide the "Access dataroom"
    // checklist item whenever the tab itself is hidden (e.g. empty dataroom pre-diligence).
    const { data: dataroom } = useDataroomQuery({
        onError() { /* user may not have dataroom access */ },
    });
    const isInvestor = permissions?.hasProjectInvestorAccess && !permissions?.isAdmin;
    const dataroomCounts = dataroom ? getDataroomCounts(dataroom.documents) : null;
    const showDataroomTab = permissions?.canReadDataroom
        && !(isInvestor && project?.dealState < DealStates.IN_DILIGENCE && !dataroomCounts?.uploaded);
    const fullChecklist = [
        { key: 'downloaded-summary', title: 'Download Deal summary', description: 'Download the Deal summary document for this deal.', isComplete: !!(activity?.downloadedSummary), linkTo: project?.id ? `/project/${project.id}/review#key-downloads` : undefined, buttonText: 'Download' },
        { key: 'downloaded-spreadsheet', title: 'Download deal spreadsheet', description: 'Download the deal spreadsheet for your records.', isComplete: !!(activity?.downloadedSpreadsheet), linkTo: project?.id ? `/project/${project.id}/review#key-downloads` : undefined, buttonText: 'Download' },
        { key: 'accessed-dataroom', title: 'Access dataroom', description: 'View or download documents from the deal dataroom.', isComplete: !!(activity?.downloadedAnyDataroomDocs), linkTo: project?.id ? `/project/${project.id}/dataroom` : undefined, buttonText: 'Go there now' },
        { key: 'supporting-info', title: 'Provide supporting information', description: 'The seller has requested some additional details about your company and experience.', isComplete: false, linkTo: project?.portfolioId ? `/portfolio/${project.portfolioId}/bidding#supporting-info` : 'bidding#supporting-info', buttonText: 'Answer now' },
        { key: 'bidding', title: 'Submit your non-binding bid ' + (bidDeadline ? 'by ' + format(parseISO(bidDeadline), 'MMM d') : ''), description: 'Submit your non-binding bid to the seller to be considered for partnership.', isComplete: false },
    ];
    const [checklistItems, setChecklistItems] = useState<any[]>(fullChecklist);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        if (project?.id) {
            getProjectRfis();
        }
        if (
            project?.dealState >= DealStates.IN_DILIGENCE ||
            project?.dealState === DealStates.DECLINED ||
            project?.dealState < DealStates.IN_REVIEW ||
            project?.status === 'DECLINED_TO_BID'
        ) {
            setShowChecklist(false);
        } else {
            setShowChecklist(true);
        }
    }, [project?.id]);

    useEffect(() => {
        // Check if bid is submitted - should be true when there ARE bids
        const hasBids = project?.projectBids && project.projectBids.length > 0;

        setChecklistItems(prevItems => {
            const updatedItems = [...prevItems];
            const biddingIndex = updatedItems.findIndex(item => item.key === 'bidding');
            if (biddingIndex !== -1) {
                updatedItems[biddingIndex] = { ...updatedItems[biddingIndex], isComplete: hasBids };
            }
            return updatedItems;
        });
    }, [project?.projectBids]);

    useEffect(() => {
        if (rfis === undefined) return;
        if (rfis.length > 0) {
            const allRfisHaveResponses = rfis.every((rfi: any) =>
                Array.isArray(rfi.responses) && rfi.responses.length > 0
            );

            setChecklistItems(prevItems => {
                const updatedItems = [...prevItems];
                const supportingInfoIndex = updatedItems.findIndex(item => item.key === 'supporting-info');
                if (supportingInfoIndex !== -1) {
                    updatedItems[supportingInfoIndex] = { ...updatedItems[supportingInfoIndex], isComplete: allRfisHaveResponses };
                }
                return updatedItems;
            });
        } else {
            setChecklistItems(prevItems => prevItems.filter(item => item.key !== 'supporting-info'));
        }
    }, [rfis]);

    useEffect(() => {
        const activity = project?.currentUserActivity;
        if (activity == null) return;
        setChecklistItems(prevItems => {
            const updated = [...prevItems];
            const summaryIdx = updated.findIndex(item => item.key === 'downloaded-summary');
            const spreadsheetIdx = updated.findIndex(item => item.key === 'downloaded-spreadsheet');
            const dataroomIdx = updated.findIndex(item => item.key === 'accessed-dataroom');
            if (summaryIdx !== -1) updated[summaryIdx] = { ...updated[summaryIdx], isComplete: !!activity.downloadedSummary };
            if (spreadsheetIdx !== -1) updated[spreadsheetIdx] = { ...updated[spreadsheetIdx], isComplete: !!activity.downloadedSpreadsheet };
            if (dataroomIdx !== -1) updated[dataroomIdx] = { ...updated[dataroomIdx], isComplete: !!activity.downloadedAnyDataroomDocs };
            return updated;
        });
    }, [project?.currentUserActivity]);

    useEffect(() => {
        if (!project?.id) return;
        const id = project.portfolioId
            ? project.portfolioId
            : (project.parentId ?? project.id);
        const fetchSummary = project.portfolioId
            ? () => portfolioApi.getPortfolioSummaryDownloadUrl(id)
            : () => projectApi.getProjectSummaryDownloadUrl(id);
        fetchSummary()
            .then((data: { presignedUrl?: string }) => setHasSummaryDoc(!!data?.presignedUrl))
            .catch(() => setHasSummaryDoc(false));
    }, [project?.id, project?.parentId, project?.portfolioId]);

    const getProjectRfis = () => {
        if (project?.id) {
            setLoading(true);
            indexRfisByProjectId(project.id).then( response => {
                setRfis(response);
                setLoading(false);
            }).catch((error) => {
                console.error('Error fetching RFIs:', error);
                setLoading(false);
            });
        }
    };

    const [summaryDownloading, setSummaryDownloading] = useState(false);
    const handleSummaryDownload = async () => {
        if (!project?.id || summaryDownloading) return;
        const projectId = project.parentId ?? project.id;
        const portfolioId = project.portfolioId ?? undefined;
        const fetchUrl = portfolioId
            ? () => portfolioApi.getPortfolioSummaryDownloadUrl(portfolioId)
            : () => projectApi.getProjectSummaryDownloadUrl(projectId);
        setSummaryDownloading(true);
        try {
            ReactGA.event({
                category: 'Downloads',
                action: 'Download Conductor summary document',
                label: project?.name || 'Unknown project',
            });
            logUserActivity({
                activityType: UserActivityTypes.SUMMARY_DOCUMENT_DOWNLOAD,
                ...(portfolioId ? { portfolioId } : { projectId }),
            });
            const data = await fetchUrl();
            if (data?.presignedUrl) {
                setChecklistItems((prev) => {
                    const next = [...prev];
                    const i = next.findIndex((item) => item.key === 'downloaded-summary');
                    if (i !== -1) next[i] = { ...next[i], isComplete: true };
                    return next;
                });
                window.location.href = data.presignedUrl;
            } else {
                toast.error('Summary document is not available.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Error occurred downloading the document. Please refresh the page and try again.');
        } finally {
            setSummaryDownloading(false);
        }
    };

    const [spreadsheetDownloading, setSpreadsheetDownloading] = useState(false);
    const handleSpreadsheetDownload = async () => {
        if (!project?.id || spreadsheetDownloading) return;
        const dealId = portfolio ? (portfolio.parentId ?? portfolio.id) : (project.parentId ?? project.id);
        const dealType = portfolio ? 'PORTFOLIO' : 'PROJECT';
        setSpreadsheetDownloading(true);
        try {
            ReactGA.event({
                category: 'Downloads',
                action: 'Download deal spreadsheet',
                label: (project ? project.name : portfolio?.name) || 'Unknown project',
            });
            logUserActivity({
                activityType: UserActivityTypes.DEAL_SPREADSHEET_DOWNLOAD,
                ...(portfolio ? { portfolioId: dealId } : { projectId: dealId }),
            });
            let data: { presignedUrl?: string } | undefined;
            try {
                data = await dealSpreadsheetApi.getDealSpreadsheetDownloadUrl(dealId, dealType);
            } catch {
                data = undefined;
            }
            if (data?.presignedUrl) {
                setChecklistItems((prev) => {
                    const next = [...prev];
                    const i = next.findIndex((item) => item.key === 'downloaded-spreadsheet');
                    if (i !== -1) next[i] = { ...next[i], isComplete: true };
                    return next;
                });
                window.location.href = data.presignedUrl;
            } else {
                if (permissions?.isAdmin) {
                    if (portfolio) {
                        await generateDealSpreadsheet(portfolio, portfolioTotals, displayBlobs);
                    } else {
                        await generateProjectSpreadsheet(formattedProject, projectCosts, projectDisplayValues);
                    }
                } else {
                    if (portfolio) {
                        await downloadPortfolioExcel(portfolio, locations, portfolioTotals, displayBlobs, permissions);
                    } else {
                        await downloadProjectExcel(formattedProject, projectCosts, projectDisplayValues, permissions);
                    }
                }
                setChecklistItems((prev) => {
                    const next = [...prev];
                    const i = next.findIndex((item) => item.key === 'downloaded-spreadsheet');
                    if (i !== -1) next[i] = { ...next[i], isComplete: true };
                    return next;
                });
            }
        } catch (err) {
            console.error(err);
            toast.error('Error occurred downloading the document. Please refresh the page and try again.');
        } finally {
            setSpreadsheetDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="card project-checklist-container">
                <Loader active inline='centered'></Loader>
            </div>
        );
    }

    const displayedItems = checklistItems.filter(
        (item) => (item.key !== 'downloaded-summary' || hasSummaryDoc === true)
            && (item.key !== 'accessed-dataroom' || showDataroomTab)
    );

    return (
        !showChecklist ? null :
            <div className="card project-checklist-container">
                <Header as='h2' className="project-checklist-title">
                    {displayedItems.every(item => item.isComplete) ? 'All checklist items complete!' : 'Your deal checklist'}
                </Header>
                <div className="portfolio-activity-checklist-list project-checklist-list">
                    {displayedItems.map((item) => (
                                    <div
                                        key={item.key}
                                        className={`portfolio-activity-checklist-item ${item.isComplete ? 'complete' : 'incomplete'}`}
                                    >
                                        <div className="portfolio-activity-checklist-item-content">
                                            {item.isComplete ? (
                                                <Icon name="check circle" color="green" />
                                            ) : (
                                                <Icon name="circle outline" />
                                            )}
                                            <span className="portfolio-activity-checklist-item-label">
                                                {item.title}
                                            </span>
                                            {!item.isComplete && (
                                                item.key === 'downloaded-summary' ? (
                                                    <>
                                                    -
                                                    <button
                                                        type="button"
                                                        className="project-checklist-action-link project-checklist-action-button"
                                                        onClick={handleSummaryDownload}
                                                        disabled={summaryDownloading}
                                                    >
                                                        {summaryDownloading ? 'Downloading…' : (item.buttonText ?? 'Download')}
                                                    </button>
                                                    </>
                                                ) : item.key === 'downloaded-spreadsheet' ? (
                                                    <>
                                                    -
                                                    <button
                                                        type="button"
                                                        className="project-checklist-action-link project-checklist-action-button"
                                                        onClick={handleSpreadsheetDownload}
                                                        disabled={spreadsheetDownloading}
                                                    >
                                                        {spreadsheetDownloading ? 'Downloading…' : (item.buttonText ?? 'Download')}
                                                    </button>
                                                    </>
                                                ) : (
                                                    <>
                                                    -
                                                    <Link
                                                        to={item.linkTo ?? (project?.portfolioId ? '/portfolio/' + project?.portfolioId + '/bidding' : 'bidding')}
                                                        className="project-checklist-action-link"
                                                    >
                                                        {item.buttonText ?? 'Submit bid'}
                                                    </Link>
                                                    </>
                                                )
                                            )}
                                        </div>
                                    </div>
                                ))}
                </div>
            </div>
    );
};

export default ProjectChecklist;